import React from 'react';
import { Plus } from 'lucide-react';
import FoodAvatar from './FoodAvatar';
import { formatMacro } from '../../utils/nutritionFormat';

/**
 * A single search-result row used by the Foods / Restaurants chain-menu /
 * Meals / Manual tabs. Mirrors RecentlyLoggedList row styling but renders
 * a SearchItem instead of a RecentFood.
 *
 * Props:
 *   item: SearchItem
 *   onSelect(item): primary tap → FoodConfirm
 *   onQuickLog(item): optional [+] button. When undefined, the [+] is
 *     hidden (e.g. on chain rows where there's nothing to quick-log).
 */
export default function FoodResultRow(props) {
  const item = props.item;
  const onSelect = props.onSelect;
  const onQuickLog = props.onQuickLog;
  if (!item) return null;
  const macros = item.macros || {};
  const subtitleParts = [];
  if (item.brand) subtitleParts.push(item.brand);
  if (item.restaurant) subtitleParts.push(item.restaurant);
  const serving = item.serving || {};
  if (serving.description) subtitleParts.push(serving.description);
  subtitleParts.push(
    'P' + formatMacro(macros.protein || 0) +
    ' · C' + formatMacro(macros.carbs || 0) +
    ' · F' + formatMacro(macros.fat || 0)
  );

  return (
    <li
      className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors"
      data-testid="food-result-row"
    >
      <button
        type="button"
        className="flex-1 flex items-center gap-3 text-left min-w-0"
        onClick={function () { onSelect && onSelect(item); }}
        data-testid="food-result-tap"
      >
        <FoodAvatar item={item} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
            <span className="text-sm font-semibold text-foreground shrink-0">
              {Math.round(macros.calories || 0)} cal
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{subtitleParts.join(' · ')}</p>
        </div>
      </button>
      {onQuickLog && (
        <button
          type="button"
          className="shrink-0 size-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-colors"
          onClick={function (e) {
            e.stopPropagation();
            onQuickLog(item);
          }}
          aria-label={'Quick log ' + item.name}
          data-testid="food-result-quick-log"
        >
          <Plus className="size-4" />
        </button>
      )}
    </li>
  );
}
